import {
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { parseContractBody, variableDisplayLabel } from "%/contractTemplate";
import type { Token } from "%/contractTemplate";
import SignatureCanvas from "./SignatureCanvas";
import DaumPostcodeModal from "./DaumPostcodeModal";

export interface InlineContractValues {
  /** 텍스트 필드 / 변수 값 — `[varName]`, `[[필드:text|key|label]]`, `[[필드:date|key|label]]`, `[guardianAddress]` 등 */
  fields: Record<string, string>;
  /** 체크박스 상태 — `[[필드:checkbox|key|label]]` */
  checkboxes: Record<string, boolean>;
  /** 서명 base64 DataURL (없으면 null) — 모든 signature 필드에 적용 */
  signature: string | null;
}

export interface InlineContractRendererRef {
  getValues(): InlineContractValues;
  /** null = OK, string = 첫 검증 실패 메시지 */
  validate(): string | null;
}

interface Props {
  contractContents: string;
  variables: Record<string, string | null | undefined>;
  /** 보호자가 미리 등록해둔 주소 (있으면 addr 토큰 초기값으로) */
  initialAddress?: string;
  /** 사장님 도장 이미지 URL — seal 토큰에 표시 */
  sealImageUrl?: string;
}

/**
 * 계약서 본문을 인라인으로 렌더하는 컴포넌트.
 *
 * 토큰별 UI:
 *   - text            → 평문 (whitespace 보존)
 *   - filled          → 밑줄 강조 텍스트 (prefilled 값)
 *   - varEmpty        → 비어있는 변수 (사장님이 정의했지만 prefill 안 된 변수) → 텍스트 인풋
 *   - addr            → AddressSearch 버튼 (오버레이로 카카오 주소 검색)
 *   - signature       → SignatureCanvas (모든 signature 필드 공통 1개 — 서명 1회만)
 *   - checkbox        → 동의 체크박스 (라벨 = field label)
 *   - text-field      → 텍스트 인풋
 *   - date-field      → date 인풋
 *   - seal            → 도장 이미지 (사장님 도장 — 읽기전용)
 *
 * 부모는 ref 로 getValues() / validate() 호출.
 */
const InlineContractRenderer = forwardRef<InlineContractRendererRef, Props>(
  ({ contractContents, variables, initialAddress, sealImageUrl }, ref) => {
    const tokens = useMemo(
      () => parseContractBody(contractContents, variables),
      [contractContents, variables],
    );

    const [fields, setFields] = useState<Record<string, string>>(() => {
      // 주소 토큰의 prefill: variables.guardianAddress > initialAddress > ""
      const init: Record<string, string> = {};
      const addrToken = tokens.find((t) => t.kind === "addr");
      if (addrToken) {
        const prefilled =
          (variables[addrToken.value] as string | undefined) ||
          initialAddress ||
          "";
        if (prefilled) init[addrToken.value] = prefilled;
      }
      return init;
    });
    const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({});
    const [signature, setSignature] = useState<string | null>(null);
    const [addrPickerKey, setAddrPickerKey] = useState<string | null>(null);

    const setField = (key: string, val: string) =>
      setFields((prev) => ({ ...prev, [key]: val }));
    const toggleCheckbox = (key: string) =>
      setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));

    useImperativeHandle(ref, () => ({
      getValues: () => ({ fields, checkboxes, signature }),
      validate: () => {
        // 모든 addr 토큰 필수
        for (const t of tokens) {
          if (t.kind === "addr" && !fields[t.value]) {
            return "주소를 입력해주세요.";
          }
        }
        // 모든 checkbox 필수 (필수 동의 — 양식 의도)
        for (const t of tokens) {
          if (t.kind === "checkbox" && !checkboxes[t.value]) {
            return `'${t.label ?? "동의"}' 항목에 체크해주세요.`;
          }
        }
        // signature 있으면 필수
        const hasSig = tokens.some((t) => t.kind === "signature");
        if (hasSig && !signature) return "서명을 해주세요.";
        return null;
      },
    }));

    return (
      <div className="text-[13px] leading-[1.8] text-[#222] whitespace-pre-wrap break-words">
        {tokens.map((token, i) => renderToken(token, i))}

        {/* 다음(카카오) 우편번호 검색 — addr 버튼 클릭 시 팝업 */}
        <DaumPostcodeModal
          open={addrPickerKey != null}
          onClose={() => setAddrPickerKey(null)}
          onSelect={(result) => {
            if (!addrPickerKey) return;
            setField(addrPickerKey, result.address);
            // 도로명 외 부가 정보도 함께 저장 (BE 전송용 — 우편번호/지번/건물명)
            setField(`${addrPickerKey}_zonecode`, result.zonecode);
            setField(`${addrPickerKey}_roadAddress`, result.roadAddress);
            setField(`${addrPickerKey}_jibunAddress`, result.jibunAddress);
            if (result.buildingName) {
              setField(`${addrPickerKey}_buildingName`, result.buildingName);
            }
            // 새로 검색했으니 상세주소는 비움
            setField(`${addrPickerKey}Detail`, "");
            setAddrPickerKey(null);
          }}
        />
      </div>
    );

    function renderToken(token: Token, i: number) {
      switch (token.kind) {
        case "text":
          return <span key={i}>{token.value}</span>;

        case "filled":
          return (
            <span
              key={i}
              className="font-medium text-[#111] border-b border-[#888] px-[1px]"
            >
              {token.filledText}
            </span>
          );

        case "varEmpty":
          return (
            <input
              key={i}
              type="text"
              value={fields[token.value] ?? ""}
              onChange={(e) => setField(token.value, e.target.value)}
              placeholder={variableDisplayLabel(token.value)}
              className="inline border-b border-[#aaa] bg-transparent text-[13px] min-w-[80px] px-1 focus:outline-none focus:border-[#FFB330] mx-[2px]"
            />
          );

        case "addr": {
          const detailKey = `${token.value}Detail`;
          const hasAddr = !!fields[token.value];
          return (
            <span
              key={i}
              className="inline-flex flex-wrap items-center gap-[6px] align-middle mx-[2px] my-[2px]"
            >
              <button
                type="button"
                onClick={() => setAddrPickerKey(token.value)}
                className={`inline-flex items-center gap-1 text-[13px] px-2 py-[3px] rounded border ${
                  hasAddr
                    ? "border-[#FFB330] bg-[#FFF7E6] text-[#111]"
                    : "border-dashed border-[#FFB330] text-[#FF9934] hover:bg-orange-50"
                }`}
                title="다음(카카오) 우편번호 검색 열기"
              >
                <span>📍</span>
                <span className="max-w-[260px] truncate text-left">
                  {fields[token.value] || "주소 검색"}
                </span>
                <span className="text-[10px] text-[#888]">▼</span>
              </button>
              {hasAddr && (
                <input
                  type="text"
                  value={fields[detailKey] ?? ""}
                  onChange={(e) => setField(detailKey, e.target.value)}
                  placeholder="상세주소 (동/호수)"
                  className="inline border-b border-[#aaa] bg-transparent text-[13px] min-w-[100px] max-w-[180px] px-1 focus:outline-none focus:border-[#FFB330]"
                />
              )}
            </span>
          );
        }

        case "checkbox":
          return (
            <span
              key={i}
              className="inline-flex items-center gap-1 mx-1 align-middle"
            >
              <input
                type="checkbox"
                id={`chk-${token.value}-${i}`}
                checked={!!checkboxes[token.value]}
                onChange={() => toggleCheckbox(token.value)}
                className="w-4 h-4 accent-[#FFB330]"
              />
              <label
                htmlFor={`chk-${token.value}-${i}`}
                className="text-[13px] text-[#444] cursor-pointer select-none"
              >
                {token.label ?? "동의"}
              </label>
            </span>
          );

        case "text-field":
          return (
            <input
              key={i}
              type="text"
              value={fields[token.value] ?? ""}
              onChange={(e) => setField(token.value, e.target.value)}
              placeholder={token.label ?? token.value}
              className="inline border-b border-[#aaa] bg-transparent text-[13px] min-w-[100px] px-1 focus:outline-none focus:border-[#FFB330] mx-[2px]"
            />
          );

        case "date-field":
          return (
            <input
              key={i}
              type="date"
              value={fields[token.value] ?? ""}
              onChange={(e) => setField(token.value, e.target.value)}
              className="inline border-b border-[#aaa] bg-transparent text-[13px] px-1 focus:outline-none focus:border-[#FFB330] mx-[2px]"
            />
          );

        case "seal":
          return sealImageUrl ? (
            <img
              key={i}
              src={sealImageUrl}
              alt={token.label ?? "도장"}
              className="inline-block align-middle max-h-12 mx-1"
            />
          ) : (
            <span
              key={i}
              className="inline-block bg-[#FFF7E6] text-[#C8102E] border border-dashed border-[#C8102E] text-[12px] rounded px-2 py-0.5 mx-1"
            >
              🖋 {token.label ?? "도장"}
            </span>
          );

        case "signature":
          return (
            <span key={i} className="block my-3">
              <span className="block text-[12px] font-semibold text-[#444] mb-2">
                {token.label ?? "보호자 서명"}
              </span>
              <SignatureCanvas
                onSignatureChange={(data) => setSignature(data)}
                width={280}
                height={120}
              />
            </span>
          );

        default:
          return null;
      }
    }
  },
);

InlineContractRenderer.displayName = "InlineContractRenderer";

export default InlineContractRenderer;
